// Reportes de control de inventario: Stock actual, Por caducar, Bajo mínimo.
// Inventario → Reportes.
import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, CalendarClock, ChevronDown, ChevronRight, Loader2, Trash2, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { useTranslation } from 'react-i18next';
import { Campo, Location, Modal, fieldCls, invRpc, invalidateInventario, locationsList, money, num, toNum } from './shared';

// ---------------------------------------------------------------------------
// Stock actual
// ---------------------------------------------------------------------------
interface StockRow {
  location_nombre: string; producto_codigo: string; producto_nombre: string; unidad_medida: string;
  unidades: number; costo_promedio: number; valor: number; stock_min: number;
  transito_entrante: number; transito_saliente: number;
  presentacion?: string | null; fracciones?: number | null;
}
// Coincidencia estilo LIKE: con % o _ se interpreta como patrón anclado (DER% = empieza
// con DER); sin comodines, "contiene". Busca en código O nombre, sin distinguir mayúsculas.
function coincide(patron: string, ...campos: string[]): boolean {
  const p = patron.trim();
  if (!p) return true;
  const camposOk = campos.map((c) => (c || '').toLowerCase());
  if (p.includes('%') || p.includes('_')) {
    const rx = new RegExp('^' + p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/%/g, '.*').replace(/_/g, '.') + '$', 'i');
    return campos.some((c) => rx.test(c || ''));
  }
  const low = p.toLowerCase();
  return camposOk.some((c) => c.includes(low));
}
export function StockActual() {
  const { t } = useTranslation('inventario');
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const [location, setLocation] = useState('');
  const [buscar, setBuscar] = useState('');
  const { data: rowsAll = [], isFetching } = useQuery<StockRow[]>({
    queryKey: ['inv-stock', location],
    queryFn: () => invRpc<StockRow[]>('inv_stock_actual', { p_location: location || null, p_producto: null }),
  });
  const rows = useMemo(
    () => rowsAll.filter((r) => coincide(buscar, r.producto_codigo, r.producto_nombre)),
    [rowsAll, buscar],
  );
  // Agrupar por bodega (con subtotales), expandible/contraíble
  const grupos = useMemo(() => {
    const cajasDe = (r: StockRow) => Number(r.unidades) / (Number(r.fracciones) || 1);
    const m = new Map<string, { nombre: string; filas: StockRow[]; unidades: number; cajas: number; entra: number; sale: number; valor: number }>();
    for (const r of rows) {
      const k = r.location_nombre || '—';
      let g = m.get(k);
      if (!g) { g = { nombre: k, filas: [], unidades: 0, cajas: 0, entra: 0, sale: 0, valor: 0 }; m.set(k, g); }
      g.filas.push(r); g.unidades += Number(r.unidades || 0); g.cajas += cajasDe(r);
      g.entra += Number(r.transito_entrante || 0); g.sale += Number(r.transito_saliente || 0); g.valor += Number(r.valor || 0);
    }
    return Array.from(m.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [rows]);
  const [colapsadas, setColapsadas] = useState<Set<string>>(new Set());
  const toggle = (n: string) => setColapsadas((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const contraerTodo = () => setColapsadas(new Set(grupos.map((g) => g.nombre)));
  const expandirTodo = () => setColapsadas(new Set());
  const exportFilas = useMemo<Celda[][]>(() => {
    const cajas = (r: StockRow) => Number(r.unidades) / (Number(r.fracciones) || 1);
    const filas: Celda[][] = rows.map((r, i) => [i + 1, r.location_nombre, r.producto_codigo, r.producto_nombre,
      r.unidad_medida, Number(r.unidades), cajas(r), Number(r.transito_entrante || 0), Number(r.transito_saliente || 0), Number(r.costo_promedio), Number(r.valor), r.stock_min ? Number(r.stock_min) : null]);
    if (filas.length) filas.push(['', t('comun.totales'), '', '', '', rows.reduce((a, r) => a + Number(r.unidades), 0),
      rows.reduce((a, r) => a + cajas(r), 0),
      rows.reduce((a, r) => a + Number(r.transito_entrante || 0), 0), rows.reduce((a, r) => a + Number(r.transito_saliente || 0), 0), null,
      rows.reduce((a, r) => a + Number(r.valor), 0), null]);
    return filas;
  }, [rows, t]);
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Boxes className="w-5 h-5" /> {t('stock.titulo')}</h1>
      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
          <Campo label={t('stock.bodega')}>
            <select className={fieldCls} value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">{t('stock.todas')}</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('stock.buscar')} className="md:col-span-2">
            <input className={fieldCls} value={buscar} onChange={(e) => setBuscar(e.target.value)}
              placeholder={t('stock.buscar_ph')} autoComplete="off" />
          </Campo>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{t('stock.existencias', { n: rows.length })}</CardTitle>
          <div className="flex items-center gap-3">
            {grupos.length > 0 && (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={expandirTodo}>{t('stock.expandir')}</Button>
                <Button variant="outline" size="sm" onClick={contraerTodo}>{t('stock.contraer')}</Button>
              </div>
            )}
            <ExportarBotones nombre="stock_actual" titulo={t('stock.titulo')}
              headers={[t('comun.n_col'), t('stock.bodega'), t('stock.codigo'), t('stock.producto'), t('stock.um'), t('stock.unidades'), t('stock.cajas'), t('stock.transito_entra'), t('stock.transito_sale'), t('stock.costo_prom'), t('stock.valor'), t('stock.minimo')]}
              filas={exportFilas} colsDerecha={[0, 5, 6, 7, 8, 9, 10, 11]} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('stock.bodega_producto')}</th><th className="py-2 pr-3">{t('stock.um')}</th>
              <th className="py-2 pr-3 text-right" title={t('stock.tt_unidades')}>{t('stock.unidades')}</th>
              <th className="py-2 pr-3 text-right" title={t('stock.tt_cajas')}>{t('stock.cajas')}</th>
              <th className="py-2 pr-3 text-right" title={t('stock.tt_entra')}>🚚 {t('stock.entra')}</th>
              <th className="py-2 pr-3 text-right" title={t('stock.tt_sale')}>🚚 {t('stock.sale')}</th>
              <th className="py-2 pr-3 text-right">{t('stock.costo_prom')}</th>
              <th className="py-2 pr-3 text-right">{t('stock.valor')}</th><th className="py-2 pr-3 text-right">{t('stock.min')}</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">{t('stock.sin_existencias')}</td></tr>}
              {grupos.map((g) => {
                const abierta = !colapsadas.has(g.nombre);
                return (
                  <Fragment key={g.nombre}>
                    <tr className="border-b bg-muted/40 cursor-pointer hover:bg-muted/60" onClick={() => toggle(g.nombre)}>
                      <td className="py-2 pr-3 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {abierta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {g.nombre} <span className="text-xs text-muted-foreground">({g.filas.length})</span>
                        </span>
                      </td>
                      <td className="py-2 pr-3"></td>
                      <td className="py-2 pr-3 text-right font-semibold">{num(g.unidades, 2)}</td>
                      <td className="py-2 pr-3 text-right font-semibold">{num(g.cajas, 2)}</td>
                      <td className="py-2 pr-3 text-right text-sky-700 dark:text-sky-300">{g.entra > 0 ? `+${num(g.entra, 2)}` : '—'}</td>
                      <td className="py-2 pr-3 text-right text-muted-foreground">{g.sale > 0 ? num(g.sale, 2) : '—'}</td>
                      <td className="py-2 pr-3"></td>
                      <td className="py-2 pr-3 text-right font-semibold">{money(g.valor)}</td>
                      <td className="py-2 pr-3"></td>
                    </tr>
                    {abierta && g.filas.map((r, i) => (
                      <tr key={`${g.nombre}-${i}`} className={`border-b last:border-0 hover:bg-muted/30 ${r.stock_min > 0 && r.unidades <= r.stock_min ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                        <td className="py-2 pr-3 pl-7">
                          <div><span className="font-mono text-xs">{r.producto_codigo}</span> {r.producto_nombre}</div>
                          <div className="text-xs text-muted-foreground">{t('stock.presentacion')}: {r.presentacion || '—'} · {t('stock.fracc')}: {r.fracciones ?? '—'}</div>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{r.unidad_medida}</td>
                        <td className="py-2 pr-3 text-right font-medium">{num(r.unidades, 2)}</td>
                        <td className="py-2 pr-3 text-right">{num(Number(r.unidades) / (Number(r.fracciones) || 1), 2)}</td>
                        <td className="py-2 pr-3 text-right text-sky-700 dark:text-sky-300">{Number(r.transito_entrante) > 0 ? `+${num(r.transito_entrante, 2)}` : '—'}</td>
                        <td className="py-2 pr-3 text-right text-muted-foreground">{Number(r.transito_saliente) > 0 ? num(r.transito_saliente, 2) : '—'}</td>
                        <td className="py-2 pr-3 text-right">{money(r.costo_promedio)}</td>
                        <td className="py-2 pr-3 text-right">{money(r.valor)}</td>
                        <td className="py-2 pr-3 text-right text-muted-foreground">{r.stock_min ? num(r.stock_min, 2) : '—'}</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Próximos a caducar
// ---------------------------------------------------------------------------
interface CaducaRow {
  location_nombre: string; producto_codigo: string; producto_nombre: string;
  lote: string; fecha_caducidad: string; dias_restantes: number; unidades: number;
}
function PorCaducar() {
  const [dias, setDias] = useState(90);
  const [q, setQ] = useState(90);
  const { data: rows = [], isFetching } = useQuery<CaducaRow[]>({
    queryKey: ['inv-caducar', q], queryFn: () => invRpc<CaducaRow[]>('inv_proximos_caducar', { p_dias: q }),
  });
  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = rows.map((r, i) => [i + 1, r.location_nombre, r.producto_codigo, r.producto_nombre,
      r.lote, r.fecha_caducidad, Number(r.dias_restantes), Number(r.unidades)]);
    if (filas.length) filas.push(['', 'Totales', '', '', '', '', null, rows.reduce((a, r) => a + Number(r.unidades), 0)]);
    return filas;
  }, [rows]);
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex items-end gap-3 max-w-md">
          <Campo label="Días hacia adelante"><input type="number" min="1" className={fieldCls} value={dias} onChange={(e) => setDias(toNum(e.target.value))} /></Campo>
          <button className="h-10 rounded-md bg-primary px-4 text-sm text-primary-foreground" onClick={() => setQ(Math.max(1, dias || 90))}>Consultar</button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Lotes próximos a vencer ({rows.length})</CardTitle>
          <ExportarBotones nombre="por_caducar" titulo="Productos por caducar"
            headers={['N°', 'Bodega', 'Código', 'Producto', 'Lote', 'Caducidad', 'Días', 'Unidades']}
            filas={exportFilas} colsDerecha={[0, 6, 7]} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3">Lote</th>
              <th className="py-2 pr-3">Caducidad</th><th className="py-2 pr-3 text-right">Días</th><th className="py-2 pr-3 text-right">Unidades</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Nada por caducar en el rango</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className={`border-b last:border-0 hover:bg-muted/40 ${r.dias_restantes < 0 ? 'bg-red-50 dark:bg-red-900/10' : r.dias_restantes <= 30 ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                  <td className="py-2 pr-3">{r.location_nombre}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.producto_codigo}</span> {r.producto_nombre}</td>
                  <td className="py-2 pr-3">{r.lote}</td>
                  <td className="py-2 pr-3">{r.fecha_caducidad}</td>
                  <td className="py-2 pr-3 text-right font-medium">{r.dias_restantes < 0 ? `vencido (${-r.dias_restantes})` : r.dias_restantes}</td>
                  <td className="py-2 pr-3 text-right">{num(r.unidades, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Baja por caducidad
//
// El reporte avisa; esto es el paso siguiente. Un lote vencido no sólo infla
// el balance con mercadería que no vale nada: mientras siga en stock, alguien
// puede despacharlo. La baja lo saca con su motivo, su asiento de merma y su
// responsable, en un movimiento y no en un ajuste hecho a mano.
// ---------------------------------------------------------------------------
interface CaducadoRow {
  location_id: string; bodega: string; producto_id: string; codigo: string; producto: string;
  unidad_medida: string | null; lote_id: string; numero_lote: string; fecha_caducidad: string;
  dias_restantes: number; unidades: number; costo_unitario: number; valor: number;
}

function DarDeBaja() {
  const qc = useQueryClient();
  const [loc, setLoc] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [confirmar, setConfirmar] = useState<{ lotes: number; valor: number; mensaje: string } | null>(null);
  const [obs, setObs] = useState('');

  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: rows = [], isFetching } = useQuery<CaducadoRow[]>({
    queryKey: ['inv-caducados', loc],
    queryFn: () => invRpc<CaducadoRow[]>('inv_caducados', { _location: loc || null, _dias: 0 }),
  });
  const { data: cfg } = useQuery<{ bloquear_venta: boolean; dias_alerta: number }>({
    queryKey: ['inv-caducidad-config'], queryFn: () => invRpc('inv_caducidad_config'),
  });

  const lineas = useMemo(() => rows.filter((r) => sel[r.lote_id]), [rows, sel]);
  const valorSel = lineas.reduce((a, r) => a + Number(r.valor), 0);

  const simular = useMutation({
    mutationFn: () => invRpc<{ lotes: number; valor: number; mensaje: string }>('inv_baja_caducados', {
      _location: lineas[0]?.location_id, _confirmar: false,
      _lineas: lineas.map((r) => ({ lote_id: r.lote_id, unidades: r.unidades })),
    }),
    onSuccess: (r) => setConfirmar(r), onError: (e) => toast.error(getErrorMessage(e)),
  });
  const dar = useMutation({
    mutationFn: () => invRpc<{ mensaje: string }>('inv_baja_caducados', {
      _location: lineas[0]?.location_id, _confirmar: true, _obs: obs || null,
      _lineas: lineas.map((r) => ({ lote_id: r.lote_id, unidades: r.unidades })),
    }),
    onSuccess: (r) => {
      toast.success(r.mensaje); setConfirmar(null); setSel({}); setObs('');
      invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-caducados'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Una baja es un documento por bodega: mezclar dos bodegas en el mismo
  // ajuste dejaría un documento que no cuadra con ninguna.
  const variasBodegas = new Set(lineas.map((r) => r.location_id)).size > 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-3">
          <Campo label="Bodega" className="w-64">
            <select className={fieldCls} value={loc} onChange={(e) => { setLoc(e.target.value); setSel({}); }}>
              <option value="">Todas mis bodegas</option>
              {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Button variant="outline" onClick={() => setSel(Object.fromEntries(rows.map((r) => [r.lote_id, true])))}
                  disabled={!rows.length}>Seleccionar todo</Button>
        </div>
        <Button disabled={!lineas.length || variasBodegas || simular.isPending}
                title={variasBodegas ? 'Selecciona lotes de una sola bodega: la baja es un documento por bodega' : ''}
                onClick={() => simular.mutate()}>
          <Trash2 className="mr-2 h-4 w-4" />Dar de baja {lineas.length ? `(${lineas.length})` : ''}
        </Button>
      </div>

      {cfg && (
        <p className="text-xs text-muted-foreground">
          {cfg.bloquear_venta
            ? 'La venta no puede despachar lotes vencidos: quedan bloqueados hasta que se den de baja.'
            : 'Atención: el bloqueo de venta de producto vencido está DESACTIVADO para esta empresa.'}
        </p>
      )}
      {variasBodegas && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Has seleccionado lotes de más de una bodega. La baja genera un documento de ajuste por bodega:
          hazlo en dos pasos.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Lotes vencidos ({rows.length}){lineas.length > 0 && <span className="ml-2 font-normal text-muted-foreground">· seleccionado {money(valorSel)}</span>}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="w-8 py-2" /><th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Producto</th>
              <th className="py-2 pr-3">Lote</th><th className="py-2 pr-3">Venció</th>
              <th className="py-2 pr-3 text-right">Hace</th><th className="py-2 pr-3 text-right">Unidades</th>
              <th className="py-2 pr-3 text-right">Valor</th>
            </tr></thead>
            <tbody>
              {!isFetching && rows.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                  No hay nada vencido en bodega. Eso es exactamente lo que se busca.
                </td></tr>)}
              {rows.map((r) => (
                <tr key={r.lote_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2"><input type="checkbox" className="h-4 w-4 rounded border-input"
                    checked={!!sel[r.lote_id]} onChange={(e) => setSel((s) => ({ ...s, [r.lote_id]: e.target.checked }))} /></td>
                  <td className="py-2 pr-3">{r.bodega}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.codigo}</span> {r.producto}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.numero_lote}</td>
                  <td className="py-2 pr-3">{r.fecha_caducidad}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-rose-600">{-r.dias_restantes} d</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(r.unidades, 2)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(r.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {confirmar && (
        <Modal title="Dar de baja por caducidad" onClose={() => setConfirmar(null)}>
          <div className="space-y-4">
            <p className="text-sm">{confirmar.mensaje}</p>
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              Se genera un documento de ajuste con motivo «Producto caducado». La mercadería sale del
              stock y su valor va a la cuenta de merma, con tu nombre en el documento.
            </p>
            <Campo label="Observación">
              <input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)}
                     placeholder="Ej.: destrucción presenciada por…" />
            </Campo>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmar(null)}>Cancelar</Button>
              <Button disabled={dar.isPending} onClick={() => dar.mutate()}>Dar de baja</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Caducidades() {
  const [tab, setTab] = useState<'reporte' | 'baja'>('reporte');
  return (
    <div className="space-y-4 p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold">
        <CalendarClock className="h-5 w-5" /> Caducidades
      </h1>
      <div className="flex gap-1 border-b">
        {([['reporte', 'Por caducar'], ['baja', 'Dar de baja']] as const).map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === k
              ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'reporte' ? <PorCaducar /> : <DarDeBaja />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bajo mínimo
// ---------------------------------------------------------------------------
interface BajoRow {
  location_nombre: string; producto_codigo: string; producto_nombre: string;
  unidades: number; stock_min: number; faltante: number;
}
export function BajoMinimo() {
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const [location, setLocation] = useState('');
  const { data: rows = [], isFetching } = useQuery<BajoRow[]>({
    queryKey: ['inv-bajo-minimo', location], queryFn: () => invRpc<BajoRow[]>('inv_bajo_minimo', { p_location: location || null }),
  });
  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = rows.map((r, i) => [i + 1, r.location_nombre, r.producto_codigo, r.producto_nombre,
      Number(r.unidades), Number(r.stock_min), Number(r.faltante)]);
    if (filas.length) filas.push(['', 'Totales', '', '', rows.reduce((a, r) => a + Number(r.unidades), 0),
      null, rows.reduce((a, r) => a + Number(r.faltante), 0)]);
    return filas;
  }, [rows]);
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><TrendingDown className="w-5 h-5" /> Stock bajo mínimo</h1>
      <Card>
        <CardContent className="pt-4 max-w-md">
          <Campo label="Bodega">
            <select className={fieldCls} value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">— Todas —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Productos a reponer ({rows.length})</CardTitle>
          <ExportarBotones nombre="bajo_minimo" titulo="Stock bajo mínimo"
            headers={['N°', 'Bodega', 'Código', 'Producto', 'Unidades', 'Mínimo', 'Faltante']}
            filas={exportFilas} colsDerecha={[0, 4, 5, 6]} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Producto</th>
              <th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Mínimo</th><th className="py-2 pr-3 text-right">Faltante</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Nada bajo mínimo</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{r.location_nombre}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.producto_codigo}</span> {r.producto_nombre}</td>
                  <td className="py-2 pr-3 text-right">{num(r.unidades, 2)}</td>
                  <td className="py-2 pr-3 text-right">{num(r.stock_min, 2)}</td>
                  <td className="py-2 pr-3 text-right font-medium text-red-600">{num(r.faltante, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
